import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';

/**
 * The TinyCMS swallow in flight: what the app shows wherever something is
 * loading. The same drawing as SwallowMark, with the wings split out so they
 * can flap (styles in src/styles/ink.css, .swallow-loader). It takes the text
 * colour around it, fades in after a beat so a quick load never flashes it,
 * and holds still for people who ask for less motion.
 *
 *   PageLoader  a whole screen (variant 'screen') or the space under the top bar
 *   Loader      in a panel, list or card (size 'block'), or beside text in a
 *               button or status line (size 'inline')
 *
 * The label keeps each place's own words ("Loading projects"); "…" is added.
 *
 * When nothing else is on the page below it (and it isn't in a dialog), a
 * loader grows so the bird flies at the middle of the screen rather than just
 * under whatever is above it (useScreenCentre).
 */

export function SwallowFlight({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 8 120 96"
      aria-hidden="true"
      className={`swallow-loader-bird ${className || ''}`}
    >
      <ellipse className="swallow-loader-shadow" cx="60" cy="100" rx="16" ry="2.5" />
      <g className="swallow-loader-body">
        <g transform="translate(60 52) rotate(-10) translate(-60 -52)">
          <g fill="currentColor">
            <path
              className="swallow-loader-wing"
              data-side="left"
              d="M55 44 C44 35 26 26 6 27 C22 36 39 48 54 55 Z"
            />
            <path
              className="swallow-loader-wing"
              data-side="right"
              d="M65 44 C76 35 94 26 114 27 C98 36 81 48 66 55 Z"
            />
            <ellipse cx="60" cy="50" rx="7.5" ry="14" />
            <circle cx="60" cy="35" r="6.5" />
            <path d="M55.5 61 L46 90 L60 72 L74 90 L64.5 61 Z" />
            <path d="M57.8 29.6 L60 25.8 L62.2 29.6 Z" />
          </g>
          <rect x="51" y="12" width="18" height="13" rx="2" fill="#2F5BEA" />
          <path
            d="M51.6 13 L60 20 L68.4 13"
            fill="none"
            stroke="#FFFFFF"
            strokeWidth="1.8"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        </g>
      </g>
    </svg>
  );
}

/** Laid out in normal flow and taking up room: content a reader would see below. */
function isVisibleContent(el: Element): boolean {
  const style = window.getComputedStyle(el);
  if (style.position === 'fixed' || style.position === 'absolute' || style.display === 'none') {
    return false;
  }
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

/** True when anything visible comes after `el` on the page (or it sits in a dialog). */
export function hasContentAfter(el: Element): boolean {
  if (el.closest('[role="dialog"], [aria-modal="true"]')) return true;
  for (let node: Element | null = el; node && node !== document.body; node = node.parentElement) {
    for (let sib = node.nextElementSibling; sib; sib = sib.nextElementSibling) {
      if (isVisibleContent(sib)) return true;
    }
  }
  return false;
}

/**
 * The height that puts this box's middle at the middle of the screen: from
 * its top down to as far below the screen's centre as its top is above it.
 * Null when it already starts past the middle, or something follows it.
 */
export function centringHeight(top: number, viewport: number): number | null {
  const h = viewport - 2 * top;
  return h > 0 ? h : null;
}

/** Grow `ref` so its contents sit at the screen's vertical centre, when the page below is empty. */
function useScreenCentre<T extends HTMLElement>(enabled: boolean) {
  const ref = useRef<T | null>(null);
  const [minHeight, setMinHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!enabled || !el) return undefined;
    const measure = () => {
      if (hasContentAfter(el)) return setMinHeight(null);
      // Measured without the height this hook added, so it never feeds on itself.
      const top = el.getBoundingClientRect().top;
      setMinHeight(centringHeight(top, window.innerHeight));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [enabled]);
  return { ref, minHeight };
}

const withEllipsis = (label: string) => `${label.replace(/(…|\.\.\.)\s*$/, '')}…`;

export function PageLoader({
  label = 'Loading',
  variant = 'section',
}: {
  label?: string;
  variant?: 'screen' | 'section';
}) {
  const { ref, minHeight } = useScreenCentre<HTMLDivElement>(variant === 'section');
  return (
    <div
      ref={ref}
      className="swallow-loader"
      data-variant={variant}
      role="status"
      aria-live="polite"
      style={minHeight ? { minHeight } : undefined}
    >
      <SwallowFlight />
      <span className="swallow-loader-label">{withEllipsis(label)}</span>
    </div>
  );
}

export function Loader({
  label = 'Loading',
  size = 'block',
  className,
  style,
}: {
  label?: string;
  size?: 'block' | 'inline';
  className?: string;
  style?: CSSProperties;
}) {
  const { ref, minHeight } = useScreenCentre<HTMLDivElement>(size === 'block');
  const body = (
    <>
      <SwallowFlight />
      <span className="swallow-loader-label">{withEllipsis(label)}</span>
    </>
  );
  const common = {
    className: `swallow-loader${className ? ` ${className}` : ''}`,
    'data-variant': size,
    role: 'status',
    'aria-live': 'polite' as const,
  };
  if (size === 'inline') {
    return (
      <span {...common} style={style}>
        {body}
      </span>
    );
  }
  return (
    <div {...common} ref={ref} style={minHeight ? { ...style, minHeight } : style}>
      {body}
    </div>
  );
}
